import {handleFileSelected} from "../../../../Utils/uploadUtils/UploadUtils.ts";
import {
    getFormattedEnvName,
    isIde,
    isMobileApp,
    sendMessageToIde,
    sendMessageToMobile
} from "../../../../Utils/misc/envNameUtils.ts";
import {ensureConnection} from "../../../../Utils/websocket/websocketUtils.ts";

export const startRecording = async (
    type: 'audio' | 'speech',
    options: any,
    miscMidRenderRefs: any,
    environment: string,
    vscode: any,
    setDynamicStateObj: (state: any) => void,
    websocketRef: any,
    setupWebsocketHelper: any
) => {
    try {
        await ensureConnection(websocketRef, setupWebsocketHelper);

        if (isIde(environment)) {
            sendMessageToIde(environment, vscode, "streamFromMic", [{}]);
        }
        else if (isMobileApp(environment)) {
            // For mobile apps, send message to React Native to start microphone
            sendMessageToMobile("streamFromMic", [{}]);
        }
        else if (getFormattedEnvName(environment) === "Web") {
            // Clean up any existing recording state
            if (miscMidRenderRefs.current.mediaRecorder?.state === 'recording') {
                miscMidRenderRefs.current.mediaRecorder.stop();
            }
            if (miscMidRenderRefs.current.stream) {
                miscMidRenderRefs.current.stream.getTracks().forEach((track: any) => track.stop());
                miscMidRenderRefs.current.stream = null;
            }
            miscMidRenderRefs.current.audioChunks = [];
            miscMidRenderRefs.current.stream = null;
            miscMidRenderRefs.current.mediaRecorder = null;

            const constraints = {
                audio: {
                    sampleRate: 16000,
                    channelCount: type === 'speech' ? 1 : 2,
                    echoCancellation: type === 'speech',
                    noiseSuppression: type === 'speech'
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            miscMidRenderRefs.current.stream = stream;

            const mediaRecorder = new MediaRecorder(stream);
            miscMidRenderRefs.current.mediaRecorder = mediaRecorder;

            if (options?.onDataAvailable) {
                mediaRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                        options.onDataAvailable(event.data);
                    }
                };
            }

            if (options?.onStop) {
                mediaRecorder.onstop = options.onStop;
            }

            mediaRecorder.start(250);
        }

        setDynamicStateObj({
            recordingType: type
        });
    } catch (error) {
        console.error('Failed to start recording:', error);
        throw error;
    }
};

export const sttTranscriptChunkHelper = (args: any, environment: string, editorRef: any, stopRecordingHelper: any, updateElementInProgress: any) => {
    if (!editorRef?.current) return;

    if (args.type === 'transcription') {
        if (args.isFinal) {
            const currentText = editorRef.current.getText();
            const newText = args.text.trim();
            if (newText) {
                const needsSpace = currentText.length > 0 && !currentText.endsWith(' ');
                editorRef.current.commands.insertContent(`${needsSpace ? ' ' : ''}${newText}`);
            }
        }
    }
    else if (args.type.toLowerCase().replace(" ", "") === "sendmessage") {
        if (editorRef.current) {
            const text = editorRef.current.getText().trim();

            if (text) {
                // Simulate Enter keydown on the textarea
                const textarea = document.querySelector('.inputEditor') as HTMLTextAreaElement;
                if (textarea) {
                    const event = new KeyboardEvent('keydown', {
                        key: 'Enter',
                        shiftKey: environment.includes('mobile'),
                        bubbles: true,
                        cancelable: true
                    });
                    textarea.dispatchEvent(event);
                }
            }
        }
    }
    else if (args.type.toLowerCase().replace(" ", "") === "stopoutput") {
        updateElementInProgress('', 'abort');
    }
    else if (args.type === 'done' || args.type === 'error' || args.type.toLowerCase().replace(" ", "") === "stoprecording") {
        stopRecordingHelper();
    }
}


const base64PcmStringsToFile = (miscMidRenderRefs: any) => {
    const sampleRate = 16000;
    const numChannels = 1;
    const bitsPerSample = 16;

    let totalByteLength = 0;
    // First, calculate the total byte length
    for (const chunk of miscMidRenderRefs.current.audioChunks) {
        const padding = (chunk.slice(-2).match(/=/g) || []).length;
        totalByteLength += (chunk.length * 3 / 4) - padding;
    }

    const combinedPCM = new Uint8Array(totalByteLength);
    let offset = 0;

    // Decode and copy data
    for (const chunk of miscMidRenderRefs.current.audioChunks) {
        const binaryString = atob(chunk);
        for (let i = 0; i < binaryString.length; i++) {
            combinedPCM[offset++] = binaryString.charCodeAt(i);
        }
    }

    // Create WAV header
    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);

    // Write WAV header
    const writeString = (view: DataView, offset: number, string: string) => {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    };

    // RIFF chunk descriptor
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + combinedPCM.length, true);
    writeString(view, 8, 'WAVE');

    // fmt sub-chunk
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
    view.setUint16(32, numChannels * (bitsPerSample / 8), true);
    view.setUint16(34, bitsPerSample, true);

    // data sub-chunk
    writeString(view, 36, 'data');
    view.setUint32(40, combinedPCM.length, true);

    // Combine header and PCM data
    const wavArray = new Uint8Array(wavHeader.byteLength + combinedPCM.length);
    wavArray.set(new Uint8Array(wavHeader), 0);
    wavArray.set(combinedPCM, wavHeader.byteLength);

    // Create WAV file
    const wavBlob = new Blob([wavArray], { type: 'audio/wav' });
    return  new File([wavBlob], `recording_${Date.now()}.wav`, { type: 'audio/wav' });
}

export const stopRecording = (
    miscMidRenderRefs: any,
    dynamicStateObjRef: any,
    userObjRef: any,
    settingsObjRef: any,
    updateSelectedMedia: any,
    websocketRef: any,
    setupWebsocketHelper: any,
    environment: string,
    vscode: any,
    setDynamicStateObj: (state: any) => void
) => {
    if (isIde(environment)) {
        sendMessageToIde(environment, vscode, "stopRecording", [{}]);
    }
    else if (isMobileApp(environment)) {
        sendMessageToMobile("stopRecording", [{}]);
        // Also notify server to stop audio recording
        if (dynamicStateObjRef.current.recordingType === "audio" && websocketRef.current?.readyState === WebSocket.OPEN) {
            websocketRef.current.send(JSON.stringify({
                functionName: 'stopAudioRecording',
                args: { recordingType: 'audio' }
            }));
        }
    }

    if (dynamicStateObjRef.current.recordingType === "audio" && miscMidRenderRefs.current.audioChunks.length > 0) {
        let audioFile;

        if (isIde(environment) || isMobileApp(environment)) {
            // For IDE and mobile apps, audio chunks are base64 PCM strings
            audioFile = base64PcmStringsToFile(miscMidRenderRefs);
        } else {
            // For web, we use WebM format with Blob chunks
            const audioBlob = new Blob(miscMidRenderRefs.current.audioChunks, { type: 'audio/webm' });
            audioFile = new File([audioBlob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
        }

        handleFileSelected(
            audioFile,
            userObjRef,
            settingsObjRef,
            updateSelectedMedia,
            websocketRef,
            setupWebsocketHelper,
            setDynamicStateObj
        );

        // Clear the chunks after handling
        miscMidRenderRefs.current.audioChunks = [];
    } else {
        // Send websocket message to notify server that audio recording has stopped
        if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
            try {
                websocketRef.current.send(JSON.stringify({
                    functionName: 'stopAudioRecording',
                    args: { recordingType: 'audio' }
                }));
            } catch (error) {
                console.error('Failed to send audio recording stop message:', error);
            }
        }
    }

    if (miscMidRenderRefs.current.mediaRecorder?.state === 'recording') {
        miscMidRenderRefs.current.mediaRecorder.stop();
    }
    if (miscMidRenderRefs.current.stream) {
        miscMidRenderRefs.current.stream.getTracks().forEach((track: any) => track.stop());
        miscMidRenderRefs.current.stream = null;
    }
    setDynamicStateObj({ recordingType: null });

    // Reset all recording state
    miscMidRenderRefs.current.audioChunks = [];
    miscMidRenderRefs.current.stream = null;
    miscMidRenderRefs.current.mediaRecorder = null;
};